import React, { FC, useEffect, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { getAuthorizationTokenForEvent } from 'ts/client/common/clientAuthService';
import { getBaseUrl } from 'ts/client/common/hooks/useClientApiFetch';
import useEvent from 'ts/client/common/hooks/useEvent';
import LoadingIndicator from 'ts/common/components/LoadingIndicator';

const watermarkBlobCache = new Map<string, string>();

// Blob URLs are kept alive for the session so repeated visits to the same watermark are instant.
// Revoke them all on page unload to avoid leaking memory.
window.addEventListener('beforeunload', () => {
    watermarkBlobCache.forEach((url) => URL.revokeObjectURL(url));
    watermarkBlobCache.clear();
});

const MAX_DIM = 1920;

interface Props {
    watermarkId: number;
    tileWidth: number;
    tileHeight: number;
}

const ClientSlideshowWatermarkOverlay: FC<Props> = ({ watermarkId, tileWidth, tileHeight }) => {
    const { brandId } = useEvent();

    // Scale cover photo dimensions to fit within MAX_DIM on the longer axis
    const scale = Math.min(MAX_DIM / Math.max(tileWidth, tileHeight), 1);
    const width = Math.round(tileWidth * scale);
    const height = Math.round(tileHeight * scale);

    const path = brandId
        ? `/brand/${brandId}/watermark/${watermarkId}?format=png&width=${width}&height=${height}`
        : null;

    const [blobUrl, setBlobUrl] = useState<string | undefined>(
        path ? watermarkBlobCache.get(path) : undefined
    );
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!path) {
            return;
        }

        const cached = watermarkBlobCache.get(path);

        if (cached) {
            setBlobUrl(cached);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const token = await getAuthorizationTokenForEvent();
                const res = await fetch(getBaseUrl() + path, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (!res.ok) {
                    if (!cancelled) {
                        // eslint-disable-next-line no-console
                        console.error(
                            `[ClientSlideshowWatermarkOverlay] Failed to load watermark: ${res.status} ${res.statusText}`
                        );
                        setFailed(true);
                    }

                    return;
                }

                const blob = await res.blob();

                if (!cancelled) {
                    const url = URL.createObjectURL(blob);
                    watermarkBlobCache.set(path, url);
                    setBlobUrl(url);
                }
            } catch (err) {
                if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.error(
                        '[ClientSlideshowWatermarkOverlay] Failed to load watermark:',
                        err
                    );
                    setFailed(true);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [path]);

    if (blobUrl) {
        return (
            <Box
                as="img"
                src={blobUrl}
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height="100%"
                objectFit="fill"
                pointerEvents="none"
                aria-hidden
            />
        );
    }

    return (
        <Flex
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            bg="black"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
            aria-hidden
        >
            {!failed && <LoadingIndicator squareClassName="theme-background-color-primary" />}
        </Flex>
    );
};

export default ClientSlideshowWatermarkOverlay;
